import {RREventType} from "../../types/RREventType";
import {Badge, Button, Col, Row, Spinner} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowCircleRight,
    faArrowLeft,
    faCheck,
    faCircleInfo, faLock,
    faMugHot, faSquare,
    faSquareCheck
} from "@fortawesome/free-solid-svg-icons";
import {dateToString, showErrorsListInToast, timeToString} from "../../tools";
import {useAppSelector} from "../../hooks";
import {useMemo, useState} from "react";
import {
    selectAllEmployees,
} from "../../../features/AppDataSlice";
import {selectCalendarData, selectCurrentEmployee, selectDepartment} from "../../../features/AppStateSlice";
import {RRCalendarDaysType, RRCalendarDayType} from "../../types/RRCalendarDayType";
import FaceControl from "../../views/calendar/FaceControl";
import {
    BusinessTimeIconButton,
    ConfirmIconButton,
    DeleteIconButton,
    ListCheckIconButton, UserPlusIconButton
} from "../../components/IconButtons";
import MCalendarItemStart from "../components/MCalendarItemStart";
import MCalendarItemReport from "../components/MCalendarItemReport";
import {RREventStatusCurrentList} from "../../types/RREventStatusType";
import {getEventName} from "../shared";
import {useWarehouseLocks, WarehouseLockNotice} from "../../warehouse/useWarehouseLocks";
import {deleteCalendarItemsWithChangeLog} from "../../views/calendar/change-log/calendarChangeLog";

type MEventDetailsPageProps = {
    selectedEvent: RREventType;
    selectedDate: Date;
    onEventClose: () => void;
}

const MEventDetailsPage: React.FC<MEventDetailsPageProps> = ({selectedEvent, selectedDate, onEventClose}) => {
    const activeDepartment = useAppSelector(selectDepartment);
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const allCalendarData = useAppSelector(selectCalendarData);
    const allEmployees = useAppSelector(selectAllEmployees);
    const [selectedItems, setSelectedItems] = useState<RRCalendarDaysType>([]);
    const [editStartItem, setEditStartItem] = useState<RRCalendarDayType | null>(null);
    const [editEndItem, setEditEndItem] = useState<RRCalendarDayType | null>(null);
    const [itemToDelete, setItemToDelete] = useState<RRCalendarDayType | null>(null);
    const [working, setWorking] = useState(false);

    const calendarData = useMemo(() => {
        const selectedDateAsStr = dateToString(selectedDate);
        setSelectedItems([]);
        return allCalendarData.filter(
            data => data.events.some(
                event => event.id === selectedEvent.id
            ) && data.date === selectedDateAsStr
        );
    }, [allCalendarData, selectedEvent]);

    const calendarItems = useMemo(() => {
        const items: RRCalendarDaysType = [];

        calendarData.forEach(data => {
            data.items.filter(
                item => item.eventId === selectedEvent.id
            ).forEach(
                item => items.push(item)
            );
        });

        items.sort(
            (a, b) => {
                if (a.isSupervisor) return -1;
                if (b.isSupervisor) return 1;
                return a.employeeName.localeCompare(b.employeeName);
            }
        )

        return items;
    }, [calendarData]);


    const warehouseStatus = useWarehouseLocks();
    const lockAccess = warehouseStatus(selectedDate, selectedEvent.id || "");
    const isWarehouseLock = lockAccess.blocked;
    const isActiveEvent = !isWarehouseLock
        && RREventStatusCurrentList.includes(selectedEvent.eventStatus);

    const isAdminOrSupervisor = isActiveEvent && (currentEmployee?.isSuperAdmin ||
        calendarItems.some(item => item.employeeId === currentEmployee?.id && item.isSupervisor));

    function editCalendarStartItem(items: RRCalendarDayType[]) {
        if (items.length === 0) {
            return;
        }
        setEditStartItem({...items[0]});
    }

    function editCalendarEndItem(items: RRCalendarDayType[]) {
        if (items.length === 0) {
            return;
        }
        // Обикновеният служител отваря собствения отчет само за преглед, извън груповото отчитане.
        setSelectedItems(isAdminOrSupervisor ? items : []);
        setEditEndItem(items[0]);
    }

    async function deleteEvent() {
        const access = warehouseStatus(itemToDelete?.eventDayStart, itemToDelete?.eventId || "", itemToDelete?.department);
        if (access.blocked || working) return;
        if (itemToDelete?.ref) {
            try {
                setWorking(true);
                await deleteCalendarItemsWithChangeLog([{
                    ref: itemToDelete.ref,
                    context: {eventNumber: selectedEvent.eventNumber, eventName: selectedEvent.eventName},
                }], currentEmployee);
                setWorking(false);
                setItemToDelete(null)
            } catch (e) {
                showErrorsListInToast("Възникна грешка", [e instanceof Error ? e.message : "Неуспешно изтриване."])
            } finally {
                setWorking(false);
                setItemToDelete(null);
            }
        }
    }

    return (
        <>
            <WarehouseLockNotice access={lockAccess}/>
            <Row className={"mb-2"}>
                <Col xs={"auto"} className={"pt-2"}>
                    <Button variant={"outline-dark"}
                            onClick={() => onEventClose()}>
                        <FontAwesomeIcon icon={faArrowLeft}/>
                    </Button>
                </Col>
                <Col className={"border shadow bg-white me-3 p-1 rounded"}>
                    <h1 className={"text-center"}>
                        {dateToString(selectedDate)}
                    </h1>
                </Col>
            </Row>

            <Row className={"mb-2 ms-2"}>
                {
                    !isActiveEvent &&
                    <Col xs={"auto"} className={"ps-0 pe-3 pt-2"}>
                        <FontAwesomeIcon icon={faLock} className={"text-danger"} size={"2x"}/>
                    </Col>
                }
                <Col className={"border shadow me-3 p-1 rounded"}
                     style={{backgroundColor: isActiveEvent ? "gold" : "lightgray"}}
                >
                    <h2 className={"text-center"}>
                        {getEventName(selectedEvent, activeDepartment)}
                    </h2>
                </Col>
            </Row>

            {
                calendarItems.map(item => {
                    const employee = allEmployees.find(
                        emp => emp.id === item.employeeId
                    );
                    const employeeName = employee ? employee.name : "Unknown Employee";
                    return (
                        <Row key={item.id}
                             className={"border rounded p-1 m-2 shadow" + (item.isSupervisor ? " bg-primary-light" : " bg-white-light")}>
                            <Row className={"mt-2 pe-0 me-0"}>
                                <Col>
                                    <div className={item.isSupervisor ? "fw-bold" : ""}>
                                        <span style={{fontSize: "12pt"}}>{employeeName}</span>
                                    </div>
                                </Col>
                                {
                                    isAdminOrSupervisor &&
                                    <Col xs={"auto"} className={"pt-2 pe-0 me-0"}>
                                        <div onClick={
                                            (e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                if (selectedItems.some(
                                                    selectedItem => selectedItem.id === item.id)) {
                                                    setSelectedItems(selectedItems.filter(
                                                        selectedItem => selectedItem.id !== item.id
                                                    ));
                                                } else {
                                                    setSelectedItems([...selectedItems, item]);
                                                }
                                            }
                                        }>
                                            <FontAwesomeIcon icon={
                                                selectedItems.some(
                                                    selectedItem => selectedItem.id === item.id) ? faSquareCheck : faSquare
                                            } size={"xl"}/>
                                        </div>
                                    </Col>
                                }
                            </Row>
                            <Row className={"mt-2 pe-0 me-0"}>
                                <Col
                                    xs={"auto"}
                                    className={"border rounded bg-white p-2 m-2"}
                                    onClick={() => editCalendarStartItem([item])}
                                >

                                    {timeToString(item.eventDayStart)}
                                </Col>
                                <Col xs={"auto"} className={"pt-3 ms-0 me-0 ps-0 pe-0"}>
                                    <FontAwesomeIcon icon={faArrowCircleRight}/>
                                </Col>
                                <Col
                                    xs={"auto"}
                                    onClick={() => editCalendarEndItem([item])}
                                    className={"border rounded bg-white p-2 m-2"}>
                                    {timeToString(item.eventDayEnd) || "..."}
                                </Col>
                                {
                                    item.restTime > 0 &&
                                    <Col xs={"auto"} className={"border rounded bg-white p-2 m-2"}>
                                        <FontAwesomeIcon icon={faMugHot}/> {item.restTime} мин
                                    </Col>
                                }
                                {
                                    item.isSupervisor &&
                                    <Col xs={"auto"} className={"me-0 pe-0 pt-2 "}>
                                        <Badge className={"p-2"}>S</Badge>
                                    </Col>
                                }
                                {
                                    item?.eventDayEnd && item?.face &&
                                    <Col xs={"auto"} className={"pt-2"}><FaceControl face={item.face}/></Col>
                                }
                                {
                                    item?.isConfirmed &&
                                    <Col xs={"auto"} className={"text-success text-nowrap pt-2"} title={item?.notes}>
                                        <FontAwesomeIcon icon={faCheck} size={"2x"}/>
                                        {item?.notes &&
                                            <span className={"text-dark"}><FontAwesomeIcon icon={faCircleInfo}/></span>}
                                    </Col>
                                }
                                <Col/>
                                {
                                    isAdminOrSupervisor &&
                                    <Col xs={"auto"} className={"text-end pe-0 me-0"}>
                                        {
                                            itemToDelete && itemToDelete.id === item.id &&
                                            (working ? <Spinner animation={"border"}/> :
                                                <ConfirmIconButton onClick={() => deleteEvent()}
                                                                   onReject={() => setItemToDelete(null)}/>)
                                        }
                                        {
                                            !itemToDelete &&
                                            <DeleteIconButton onClick={() => {
                                                setItemToDelete(item);
                                            }}/>
                                        }
                                    </Col>
                                }
                            </Row>
                        </Row>
                    );
                })
            }
            <Row className={"mt-3 mb-5"}>
                {
                    isAdminOrSupervisor &&
                    <Col xs={"auto"} className={"ps-4"}>
                        <UserPlusIconButton onClick={() => {
                            setEditStartItem({
                                eventId: selectedEvent.id!,
                                isSupervisor: false,
                                employeeId: "",
                                employeeName: "",
                                notes: "",
                                department: activeDepartment,
                                eventDayStart: selectedEvent.firstDate ?? selectedDate,
                                isConfirmed: false,
                                restTime: 0,
                                bonus: 0,
                                isMontage: activeDepartment === "montage",
                                dayType: "WORKDAY",
                                isWarehouse: selectedEvent.id === "WAREHOUSE",
                            })
                        }} iconSize={"xl"} />
                    </Col>
                }
                <Col></Col>
                {
                    selectedItems.length > 0 && isAdminOrSupervisor && <>
                        <Col xs={"auto"}>
                            <BusinessTimeIconButton
                                variant={"success"}
                                onClick={() => {
                                    editCalendarEndItem(selectedItems)
                                }} small={false} iconSize={"xl"}/>
                        </Col>

                        {/*<Col xs={"auto"}>*/}
                        {/*    <DeleteIconButton*/}
                        {/*        variant={"danger"}*/}
                        {/*        onClick={() => {*/}
                        {/*        }} small={false}/>*/}
                        {/*</Col>*/}
                    </>
                }
                {
                    isAdminOrSupervisor &&
                    <Col xs={"auto"} className={"pe-4"}>
                        <ListCheckIconButton onClick={() => {
                            if (selectedItems.length > 0) {
                                setSelectedItems([]);
                            } else {
                                setSelectedItems([...calendarItems]);
                            }
                        }} small={false} iconSize={"xl"}/>
                    </Col>
                }
            </Row>

            <Row className={"mb-5"}>
                <Col>&nbsp;</Col>
            </Row>

            {
                editStartItem &&
                (isAdminOrSupervisor || editStartItem.employeeId === currentEmployee?.id) &&
                <MCalendarItemStart
                    selectedDate={selectedDate}
                    event={selectedEvent}
                    item={editStartItem}
                    onDone={() => {
                        setEditStartItem(null);
                    }}
                    readonly={!isAdminOrSupervisor}
                />
            }

            {
                editEndItem && selectedItems.length === 0 &&
                (isAdminOrSupervisor || editEndItem.employeeId === currentEmployee?.id) &&
                <MCalendarItemReport
                    isSupervisor={isAdminOrSupervisor}
                    event={selectedEvent}
                    items={[editEndItem]}
                    onDone={() => {
                        setEditEndItem(null);
                    }}
                    readonly={!isAdminOrSupervisor}
                />
            }

            {
                selectedItems.length > 0 && isAdminOrSupervisor && editEndItem &&
                <MCalendarItemReport
                    isSupervisor={isAdminOrSupervisor}
                    event={selectedEvent}
                    items={selectedItems}
                    onDone={() => {
                        setSelectedItems([]);
                        setEditEndItem(null);
                    }}
                    readonly={false}
                />
            }
        </>
    );
};

export default MEventDetailsPage;
